import { io, Socket } from "socket.io-client";
import { socketToken, environments } from "../environments";
import toast from "react-hot-toast";
import { getOne } from "./db";
import { Tabelas } from "../interfaces";

interface LogEntry {
    id_origem: string;
    nome_tabela: string;
    [key: string]: any;
}

interface Logs {
    inserts: LogEntry[];
    updates: LogEntry[];
    deletes: LogEntry[];
}

type SocketOptions = {
    enableSinter?: boolean;
};

type ProcessarIntegracaoOptions = {
    sinterOnly?: boolean;
};

let socket: Socket | null = null;

const MUNICIPIOS_COM_INTEGRACAO_SOCKET = ["maracanau", "vitoriadaconquista", "riobranco", "mariana", "horizonte"];

const AMBIENTES_INTEGRACAO_SOCKET_POR_MUNICIPIO: Partial<Record<string, readonly string[]>> = {
  horizonte: ["topocartDev"],
};

export function permiteIntegracaoSocketNoAmbiente(municipio: string | undefined | null): boolean {
  const municipioNormalizado = municipio?.toLowerCase();
  const ambienteAtual = process.env.REACT_APP_AMBIENTE || "topocartDev";
  const ambientesPermitidos = municipioNormalizado
    ? AMBIENTES_INTEGRACAO_SOCKET_POR_MUNICIPIO[municipioNormalizado]
    : undefined;

  return !ambientesPermitidos || ambientesPermitidos.includes(ambienteAtual);
}

export function usaIntegracaoSocket(municipio: string | undefined | null): boolean {
  const municipioNormalizado = municipio?.toLowerCase();

  return (
    permiteIntegracaoSocketNoAmbiente(municipioNormalizado) &&
    !!municipioNormalizado &&
    MUNICIPIOS_COM_INTEGRACAO_SOCKET.includes(municipioNormalizado)
  );
}

export function getSocket(options: SocketOptions = {}): Socket | null {
  const municipio = localStorage.getItem("municipio")?.toLowerCase();

  if (socket) return socket;

  if (!permiteIntegracaoSocketNoAmbiente(municipio)) {
    return null;
  }

  if (usaIntegracaoSocket(municipio) || options.enableSinter) {
    socket = io(environments.urlApiIntegracao, {
      transports: ["websocket"],
      withCredentials: true,
      auth: { token: socketToken },
    });

    socket.on("connect_error", () =>
      toast.error(
        "Falha ao conectar com o servidor de integração. Tente novamente mais tarde ou use 'Sincronizar Dados'."
      )
    );
  }

  return socket;
}

export async function processarIntegracaoSocket(
    municipio: string,
    logs: Logs,
    options: ProcessarIntegracaoOptions = {},
) {
    if (!permiteIntegracaoSocketNoAmbiente(municipio)) {
        return;
    }

    const deveProcessar = usaIntegracaoSocket(municipio) || options.sinterOnly;
    if (!deveProcessar) {
        return;
    }

    switch (usaIntegracaoSocket(municipio) || options.sinterOnly ? 'integracaoSocket' : municipio) {
        case 'integracaoSocket': {
            if (!socket?.connected) {
                toast.error(
                    "Falha ao conectar com o servidor de integração. Tente novamente mais tarde ou use 'Sincronizar Dados'."
                );
                return;
            }

            const promises: Promise<void>[] = [];
            const emittedKeys = new Set<string>();

            const shouldEmit = (payload: { id: any; municipio: string; camada?: string; isAtributos?: boolean; method?: string; sinterOnly?: boolean }) => {
                const camadaKey = payload.camada || 'unknown';
                const key = `${payload.municipio}|${camadaKey}|${payload.id}|${payload.method || 'PUT'}|${payload.isAtributos ? 'attr' : 'norm'}|${payload.sinterOnly ? 'sinter' : 'normal'}`;
                if (emittedKeys.has(key)) return false;
                emittedKeys.add(key);
                return true;
            };

            const processEntry = (entry: any, tipo: string) => {
                if (entry.nome_tabela === 'imobiliario_pessoa') {
                    return;
                }

                const id = entry.id_origem ?? entry.fid;

                if (typeof id === "string" && id.includes("-")) {
                    return;
                }

                const houveAlteracaoDeAtributo = !!entry.nome_campo;
                const camadaNormalizada = (() => {
                    if (municipio !== 'vitoriadaconquista' || !houveAlteracaoDeAtributo) {
                        return entry.nome_tabela;
                    }
                    if (typeof entry.nome_tabela === 'string') {
                        if (entry.nome_tabela.endsWith('_atributos')) {
                            return entry.nome_tabela.replace('_atributos', '');
                        }
                        if (entry.nome_tabela.endsWith('_atributo')) {
                            return entry.nome_tabela.replace('_atributo', '');
                        }
                    }
                    return entry.nome_tabela;
                })();
                const trataComoAtributo =
                    houveAlteracaoDeAtributo &&
                    municipio !== 'vitoriadaconquista';

                const responsePayload =
                    tipo === 'deletes'
                        ? { method: 'DELETE' }
                        : !trataComoAtributo && houveAlteracaoDeAtributo
                            ? (() => {
                                const tabelaDestino = (camadaNormalizada ?? entry.nome_tabela) as Tabelas;
                                const dadoCompleto = getOne(String(id), tabelaDestino);
                                return dadoCompleto ?? { method: 'PATCH', observacao: 'Atualização completa para atributo alterado' };
                            })()
                            : entry;

                const payload = {
                    id,
                    municipio,
                    camada: camadaNormalizada,
                    method: getWebhookMethod(tipo),
                    response: responsePayload,
                    isAtributos: trataComoAtributo,
                    sinterOnly: options.sinterOnly,
                };

                if (!shouldEmit(payload)) {
                    return;
                }

                const deveEsperar = (() => {
                    const isUpdateDeAtributoVDC =
                        municipio === 'vitoriadaconquista' &&
                        tipo === 'updates' &&
                        houveAlteracaoDeAtributo;

                    if (isUpdateDeAtributoVDC) {
                        return false;
                    }

                    return (
                        !trataComoAtributo ||
                        (trataComoAtributo &&
                            camadaNormalizada === 'imobiliario' &&
                            tipo === 'inserts')
                    );
                })();
                if (deveEsperar) {
                    const promise = new Promise<void>((resolve, reject) => {
                        socket.emit('startWebhook', payload, (ack: { success: boolean }) => {
                            ack?.success ? resolve() : reject(new Error(`Erro ao integrar ${entry.nome_tabela} ${id}`));
                        });
                    });
                    promises.push(promise);
                } else {
                    socket.emit('startWebhook', payload);
                }
            };

            ['inserts', 'updates', 'deletes'].forEach((tipo) => {
                logs[tipo]?.forEach((entry) => {
                    if (entry.nome_tabela === 'pessoa') {
                        processEntry(entry, tipo);
                    }
                });
            });

            let results = await Promise.allSettled(promises);
            results.forEach((r) => {
                if (r.status === 'rejected') console.error('[SOCKET] Erro em webhook (pessoa):', r.reason);
            });
            promises.length = 0;

            ['inserts', 'updates', 'deletes'].forEach((tipo) => {
                logs[tipo]?.forEach((entry) => {
                    if (!['pessoa', 'imobiliario_pessoa'].includes(entry.nome_tabela)) {
                        processEntry(entry, tipo);
                    }
                });
            });

            const houveVinculoPessoa =
                logs.inserts.some(e => e.nome_tabela === 'imobiliario_pessoa') ||
                logs.updates.some(e => e.nome_tabela === 'imobiliario_pessoa') ||
                logs.deletes.some(e => e.nome_tabela === 'imobiliario_pessoa');

            if (houveVinculoPessoa) {
                const idImobUpdates = logs.updates.filter(
                    (u) =>
                        u.nome_tabela === 'imobiliario_pessoa' &&
                        u.nome_campo === 'id_imobiliario' &&
                        u.valor_campo
                );

                const idsImobiliario = Array.from(new Set(idImobUpdates.map((u) => String(u.valor_campo))));

                idsImobiliario.forEach((id) => {
                    if (typeof id === "string" && id.includes("-")) {
                        return;
                    }
                    const payload = {
                        id,
                        municipio,
                        camada: 'imobiliario',
                        method: 'PUT',
                        sinterOnly: options.sinterOnly,
                        response: { method: 'PATCH', observacao: 'vínculo pessoa alterado' }
                    };

                    if (!shouldEmit(payload)) {
                        return;
                    }

                    const promise = new Promise<void>((resolve, reject) => {
                        socket.emit('startWebhook', payload, (ack: { success: boolean }) => {
                            if (ack?.success) {
                                resolve();
                            } else {
                                reject(new Error(`Erro ao integrar imobiliario (via vínculo): ${id}`));
                            }
                        });
                    });

                    promises.push(promise);
                });
            }

            results = await Promise.allSettled(promises);
            results.forEach((r) => {
                if (r.status === 'rejected') console.error('[SOCKET] Erro em webhook (outros):', r.reason);
            });

            break;
        }

        default:
            break;
    }
}

export { socket };

function getWebhookMethod(tipo: string): 'POST' | 'PUT' | 'DELETE' {
    switch (tipo) {
        case 'inserts':
            return 'POST';
        case 'deletes':
            return 'DELETE';
        case 'updates':
        default:
            return 'PUT';
    }
}
